// NC1 signup bootstrap — "identity born at website signup" (AWS-PORT.md
// chunk 1; v3 per XWING.md — X-Wing identity + epoch keys; v2 was
// EPOCH-KEYPAIR.md).
//
// Called from Login.onAuthentication (the universal post-auth funnel, so
// email/password AND Google/hosted-UI signups take the same path — no OAuth
// special-case). For a brand-new account it:
//   1. probes GET /e2ee/account (also yields the altAccountId),
//   2. generates the identity (X-Wing + Ed25519) and the epoch-0 X-Wing
//      KEYPAIR (uploaders encapsulate per-record keys to its 1216-byte
//      public half; only members ever hold the 32-byte seed),
//   3. wraps the identity secrets under a fresh 160-bit recovery code
//      (always) and, where the platform supports WebAuthn PRF, under a new
//      passkey too,
//   4. publishes the ONE-transaction bootstrap (bundle + genesis device list
//      + owner-only member list carrying the epoch public key + the epoch
//      secret sealed to the owner + genesis log triplet),
//   5. caches the unwrapped identity in the IndexedDB keystore,
//   6. returns the recovery code for the require-acknowledge dialog.
//
// NEVER blocks signup: any failure sets a retry marker and the next login
// retries. Existing accounts without the marker are untouched (Virgil
// accounts keep working exactly as before — the coexistence prime directive).

import E2eeApi from '@/aws/e2ee/E2eeApi'
import { getSodium } from './sodium'
import * as Keystore from './Keystore'
import { forgetPinnedHead } from './Decryptor'
import { readmitDevices } from './Rotate'
import { prfProbablySupported, generatePrfSaltB64 } from './PasskeyPrf'
import { passkeyMode } from './PasskeyPolicy'
import { wrapSecrets, wrapToWebauthn } from './shared/kek.js'
import { generateRecoveryCode, recoveryCodeDisplay } from './shared/recovery.js'
import { sealInviteKey } from './InviteKeys'
import {
  b64, epochKeypair, sealBox, deviceListSignedBytes, memberListSignedBytes,
} from './shared/envelope.js'
import { writeBundle } from './Wraps'
import {
  KIND_IDENTITY, KIND_DEVICE_LIST, KIND_MEMBER_LIST,
  docHashIdentity, docHashDeviceList, docHashMemberList,
  signEntry, entryHash,
} from './shared/translog.js'

const pendingKey = (email) => `nc1.bootstrap.pending.${email}`

// identityVersion is 1 at signup; a viewer-only identity RESET (finding ⑯)
// re-genesises at old+1 with the same document shapes.
function buildBootstrap(sodium, { email, altAccountId, wraps, idSign, idEnc, epoch, identityVersion = 1, inviteKeys = null }) {
  const ts = Date.now()

  const identityBundle = {
    email,
    userId: altAccountId,
    identityVersion,
    idEncPk: b64(sodium, idEnc.publicKey),
    idSignPk: b64(sodium, idSign.publicKey),
    wraps,
    // Invite keys claimed before this identity existed (INVITE-LINK-PASSKEY-
    // FIRST.md §1.5 step 4), already sealed to the new identity key.
    ...(inviteKeys && inviteKeys.length ? { inviteKeys } : {}),
  }

  // Genesis device list is EMPTY: the web is an identity holder, never an
  // upload device. Windows machines join later by having their signing key
  // admitted to this list.
  const deviceList = { userId: altAccountId, version: 1, devices: [] }
  deviceList.identitySig = b64(sodium,
    sodium.crypto_sign_detached(deviceListSignedBytes(deviceList), idSign.privateKey))

  const memberList = {
    accountId: altAccountId,
    version: 1,
    keyEpoch: 0,
    // The epoch-0 PUBLIC key (v3: 1216-byte X-Wing) — the only key an uploader needs.
    epochPk: b64(sodium, epoch.epochPk),
    members: [{
      userId: altAccountId,
      email,
      idEncPk: identityBundle.idEncPk,
      idSignPk: identityBundle.idSignPk,
      role: 'owner',
      joinedAt: ts,
    }],
  }
  memberList.ownerSig = b64(sodium,
    sodium.crypto_sign_detached(memberListSignedBytes(memberList), idSign.privateKey))

  // The epoch-0 SECRET (its 32-byte seed) in a v3 sealed box to the owner's
  // X-Wing identity key, under the ADK context (XWING.md §3.3) — the row
  // shape is unchanged since v1, only its contents moved. `epochSince`
  // stamps the mint time for retention pruning (EpochRetention.js).
  const adkEnvelopes = [{
    accountId: altAccountId,
    keyEpoch: 0,
    userId: altAccountId,
    sealedAdk: b64(sodium, sealBox(sodium, epoch.seed, idEnc.publicKey,
      { accountId: altAccountId, keyEpoch: 0, userId: altAccountId })),
    epochSince: ts,
  }]

  // Genesis log triplet (lateGenesis omitted — fresh account at signup),
  // mirroring shared/translog.js commitDocs.
  const log = []
  let seq = 0
  let prevHash = ''
  const push = (core, newSignerSk = null) => {
    const entry = signEntry(sodium,
      { accountId: altAccountId, seq, prevHash, ts, ...core }, idSign.privateKey, newSignerSk)
    log.push(entry)
    prevHash = entryHash(sodium, entry)
    seq += 1
  }
  push({
    kind: KIND_IDENTITY, docVersion: identityVersion,
    docHash: docHashIdentity(sodium, identityBundle),
    signerPk: identityBundle.idSignPk, newSignerPk: identityBundle.idSignPk,
  }, idSign.privateKey)
  push({
    kind: KIND_DEVICE_LIST, docVersion: 1,
    docHash: docHashDeviceList(sodium, deviceList),
    signerPk: identityBundle.idSignPk,
  })
  push({
    kind: KIND_MEMBER_LIST, docVersion: 1,
    docHash: docHashMemberList(sodium, memberList),
    signerPk: identityBundle.idSignPk,
  })

  return { email, identityBundle, deviceList, memberList, adkEnvelopes, log }
}

/**
 * Run the signup bootstrap when appropriate. Returns one of:
 *   { status: 'skipped' }  — not configured / not a new account / no marker
 *   { status: 'exists' }   — account already has an identity
 *   { status: 'created', recoveryCode, recoveryCodeGrouped, recoveryCodeMintedAt, passkeyRegistered }
 *   { status: 'failed' }   — logged, marker set, retried at next login
 * Never throws, never blocks signup.
 *
 * `acquirePasskey({ email, required, prfSaltB64, fallback })` is the UI hook
 * (the consent dialog): it returns { credentialIdB64, prfOutput } after a
 * successful ceremony, or null when the user took the backup-code fallback.
 *
 * `pendingInviteKeys` (invite link, INVITE-LINK-PASSKEY-FIRST.md §1.5): invite
 * keys unwrapped from a link BEFORE this identity existed, as plain
 * { ownerUserId, memberUserId, encPk, signPk, encSk, signSk }; they are sealed
 * to the new identity and published inside the genesis bundle.
 */
export async function ensureSignupBootstrap({ email, isNewAccount, acquirePasskey, pendingInviteKeys = [] }) {
  try {
    if (!E2eeApi.isConfigured()) {
      return { status: 'skipped' }
    }
    if (!isNewAccount && !localStorage.getItem(pendingKey(email))) {
      return { status: 'skipped' }
    }

    const probe = (await E2eeApi.getAccount(email)).data
    if (probe.hasIdentity) {
      localStorage.removeItem(pendingKey(email))
      return { status: 'exists' }
    }
    if (!probe.altAccountId) {
      throw new Error('account has no altAccountId')
    }
    const altAccountId = probe.altAccountId

    const sodium = await getSodium()
    const minted = await mintIdentity(sodium, { email, acquirePasskey, identityVersion: 1 })

    const inviteKeys = (pendingInviteKeys || []).map((k) => sealInviteKey(sodium, k, minted.secrets.id_enc_pk))
    const payload = buildBootstrap(sodium, {
      email, altAccountId, wraps: minted.wraps, idSign: minted.idSign, idEnc: minted.idEnc, epoch: minted.epoch,
      inviteKeys,
    })
    await E2eeApi.bootstrapAccount(payload)
    // A browser that pinned this account's transparency head under a PREVIOUS
    // identity (a dev re-genesis: partition wiped, bootstrapped again) would
    // otherwise refuse the fresh chain as a rollback until the pin is cleared
    // by hand (2026-09-15). The reset path already forgets it the same way.
    forgetPinnedHead(altAccountId)

    await Keystore.saveIdentity(email, { secrets: minted.secrets, altAccountId, email })
    localStorage.removeItem(pendingKey(email))

    console.log(`nc1 bootstrap complete for ${email} (passkey=${minted.passkeyRegistered})`)
    return {
      status: 'created',
      recoveryCode: minted.recoveryCode,
      recoveryCodeGrouped: minted.recoveryCode ? recoveryCodeDisplay(minted.recoveryCode) : null,
      recoveryCodeMintedAt: minted.recoveryCodeMintedAt,
      passkeyRegistered: minted.passkeyRegistered,
    }
  } catch (e) {
    console.log('nc1 bootstrap failed (will retry at next login)')
    console.log(e)
    try {
      localStorage.setItem(pendingKey(email), String(Date.now()))
    } catch { /* storage unavailable — retry naturally on a later login */ }
    return { status: 'failed' }
  }
}

/**
 * Generate a brand-new identity (X-Wing + Ed25519), the epoch-0 keypair, and
 * the identity's wrap set. PASSKEY-FIRST (INVITE-LINK-PASSKEY-FIRST.md §2.2,
 * 2026-10-08): where the platform supports WebAuthn PRF the passkey ceremony
 * runs first and cannot be skipped; only when no passkey could be made (no
 * platform support, or the ceremony failed and the user chose the fallback)
 * is a 160-bit backup code minted and shown. The server requires at least one
 * wrap, not specifically the recovery one.
 * Shared by the signup bootstrap and the identity resets.
 */
async function mintIdentity(sodium, { email, acquirePasskey, identityVersion }) {
  const idSign = sodium.crypto_sign_keypair()
  // v3: the identity encryption key is X-Wing (XWING.md §3.4) — the same
  // keypair shape as an epoch key: a 1216-byte public key and a 32-byte seed
  // as the secret (`id_enc_sk` in the wrapped secrets JSON).
  const idEncKp = epochKeypair(sodium)
  const idEnc = { publicKey: idEncKp.epochPk, seed: idEncKp.seed }
  const epoch = epochKeypair(sodium)
  const secrets = {
    id_enc_pk: b64(sodium, idEnc.publicKey),
    id_enc_sk: b64(sodium, idEnc.seed),
    id_sign_pk: b64(sodium, idSign.publicKey),
    id_sign_sk: b64(sodium, idSign.privateKey),
    identity_version: identityVersion,
  }
  const secretsBytes = new TextEncoder().encode(JSON.stringify(secrets))
  const wraps = {}

  // Passkey FIRST. The WebAuthn ceremony runs inside the consent dialog's
  // click handler (a real user gesture); `acquirePasskey` resolves with the
  // material, or null when the user took the "Use a backup code instead"
  // fallback after a failure. `required: true` = no Skip button; this is the
  // opt-in ceremony itself, independent of VUE_APP_E2EE_PASSKEY_MODE (which
  // still governs the login-time catch-up for older accounts).
  let passkeyRegistered = false
  try {
    if (acquirePasskey && await prfProbablySupported()) {
      const prfSaltB64 = generatePrfSaltB64()
      const material = await acquirePasskey({
        email,
        required: true,
        prfSaltB64,
        fallback: 'backup-code',
      })
      if (material) {
        wraps.webauthn = [wrapToWebauthn(sodium, material.prfOutput, secretsBytes, {
          credentialId: material.credentialIdB64,
          prfSalt: prfSaltB64,
        })]
        sodium.memzero(material.prfOutput)
        passkeyRegistered = true
        // Remember that THIS browser minted the credential (UI hint only).
        await Keystore.recordLocalPasskey(email, material.credentialIdB64)
      }
    }
  } catch (e) {
    console.log('passkey registration skipped:', e && e.message)
  }

  // Backup code — only when no passkey protects the identity. It is shown
  // exactly once (the require-acknowledge dialog) and never persisted.
  let recoveryCode = null
  let recoveryCodeMintedAt = null // ms; stamped into the backup-code file (RecoveryCodeFile.js)
  if (!passkeyRegistered) {
    recoveryCode = generateRecoveryCode(sodium)
    recoveryCodeMintedAt = Date.now()
    wraps.recovery = wrapSecrets(sodium, recoveryCode, secretsBytes)
  }

  return { idSign, idEnc, epoch, secrets, wraps, recoveryCode, recoveryCodeMintedAt, passkeyRegistered }
}

/**
 * Invite link (INVITE-LINK-PASSKEY-FIRST.md §1.5 step 5): a recipient who
 * ALREADY has an identity stores a claimed invite key in their bundle —
 * sealed to their identity key, custody-signed, wraps untouched (nothing is
 * re-wrapped). Replaces any earlier key from the same owner.
 */
export async function addInviteKeyToBundle({ email, cached, inviteKey }) {
  email = String(email).toLowerCase()
  const sodium = await getSodium()
  const sealed = sealInviteKey(sodium, inviteKey, cached.secrets.id_enc_pk)
  // Conditional on the rev the bundle was read with and re-applied on a
  // conflict (Wraps.writeBundle, 2026-10-08): a claimed invite link is
  // single-use, so losing this entry to a concurrent wrap write would lose
  // the recipient's access for good.
  const { bundle } = await writeBundle(email, (current) => ({
    ...current,
    inviteKeys: (current.inviteKeys || []).filter((k) => k && k.ownerUserId !== inviteKey.ownerUserId).concat([sealed]),
  }), {
    cached,
    mismatch: 'This browser\'s encryption keys are not the ones published for your account — unlock it again first.',
  })
  return { inviteKeys: bundle.inviteKeys.length }
}

/**
 * Finding ⑯: a VIEWER-ONLY account (a report recipient) that lost every unlock
 * method — no passkey, no recovery code, no linked browser — publishes a
 * brand-new identity at identityVersion old+1. The service re-genesises the
 * account's partition (it refuses accounts with devices or other members).
 * Nothing is recoverable from the old identity and nothing needs to be: the
 * account holds no encrypted data of its own. Every owner who shared reports
 * with it keeps the OLD keys in their member list and must re-approve them
 * (one click, Emails.vue "needs re-approval").
 *
 * OWNER scope (OWNER-RESET.md, 2026-09-22): `scope: 'owner'` plus `idToken`
 * from a fresh sign-in (Reauth.js). The account HAS monitored devices and
 * members; the service re-authenticates, refuses a locked account, wipes
 * the partition in phases, re-genesises it, queues the history purge and
 * tells everyone. A reset that died mid-wipe (probe `reset.state ===
 * 'wiping'`, no identity) is finished by the same call — the previous
 * version then comes from the marker instead of the (gone) bundle.
 *
 * `readmitDevices` (owner scope; auto re-admission, 2026-09-22) = the
 * devices that were live before the reset and are still registered,
 * `[{ deviceId, devSignPk }]`, snapshotted by the caller BEFORE this call.
 * They are put back on the new device list right after the genesis, so
 * they rejoin on their next tick with no approval step; a failure here is
 * logged, not thrown (those devices re-post and are approved by name).
 *
 * Throws on failure (the caller shows the error). Returns
 * { recoveryCode, recoveryCodeGrouped, recoveryCodeMintedAt, passkeyRegistered,
 *   identityVersion, resetAt, purge, resumed, readmitted } — `purge` is the job the service
 * queued ({ jobId, state, keepFromMs }) or null when the caller must start
 * the purge itself (custody-signed; it holds the new key now); `readmitted`
 * is the device ids put back on the list (empty when none / it failed).
 */
export async function resetIdentity({ email, acquirePasskey, scope = 'viewer', idToken = null, readmitDevices: readmit = [] }) {
  email = String(email).toLowerCase()
  if (!E2eeApi.isConfigured()) throw new Error('Encryption is not available for this environment.')
  const owner = scope === 'owner'

  const probe = (await E2eeApi.getAccount(email)).data
  if (!probe.altAccountId) throw new Error('This account has no encryption identity to reset.')
  const altAccountId = probe.altAccountId
  let previousVersion
  if (probe.hasIdentity) {
    const current = (await E2eeApi.getIdentityBundle(email)).data
    previousVersion = Number.isSafeInteger(current?.identityVersion) ? current.identityVersion : 1
  } else if (owner && probe.reset && probe.reset.state === 'wiping'
      && Number.isSafeInteger(probe.reset.previousIdentityVersion)) {
    // Resuming: the old identity is already gone, the marker remembers its version.
    previousVersion = probe.reset.previousIdentityVersion
  } else {
    throw new Error('This account has no encryption identity to reset.')
  }
  const identityVersion = previousVersion + 1

  const sodium = await getSodium()
  const minted = await mintIdentity(sodium, { email, acquirePasskey, identityVersion })
  const payload = buildBootstrap(sodium, {
    email, altAccountId, wraps: minted.wraps, idSign: minted.idSign, idEnc: minted.idEnc, epoch: minted.epoch,
    identityVersion,
  })
  let response
  try {
    const res = await E2eeApi.resetAccountIdentity(owner ? { ...payload, scope: 'owner', idToken } : payload)
    response = (res && res.data) || {}
  } catch (e) {
    // An owner reset that failed on the wire may still have landed: the route
    // runs a batched wipe under API Gateway's 29 s cap. Refusals are final;
    // anything else is checked against the account before giving up, because
    // the keys minted above are the only copy of the new identity.
    if (!owner || isFinalRefusal(e)) throw e
    const after = await E2eeApi.getAccount(email).then((r) => r.data).catch(() => null)
    const bundle = after && after.hasIdentity && after.identityVersion === identityVersion
      ? await E2eeApi.getIdentityBundle(email).then((r) => r.data).catch(() => null)
      : null
    if (!bundle || bundle.idSignPk !== payload.identityBundle.idSignPk) throw e
    console.log('nc1 owner reset: the request failed on the wire but the reset landed - continuing', e && e.message)
    response = { identityVersion, resetAt: after.reset && after.reset.at, purge: null, resumed: false }
  }

  // This browser now holds the new identity. Any pinned transparency head
  // for the account belongs to the chain that was just replaced (the
  // Decryptor's store is the one that matters — fixed 2026-09-10, it used
  // to clear the vendored module's unused key).
  forgetPinnedHead(altAccountId)
  await Keystore.saveIdentity(email, { secrets: minted.secrets, altAccountId, email })
  localStorage.removeItem(pendingKey(email))

  // Auto re-admission: the devices that were live before go straight back on
  // the new list, under the new key — no re-approval by name needed.
  let readmitted = []
  if (owner && readmit.length) {
    try {
      readmitted = (await readmitDevices({ email, devices: readmit })).readmitted
      console.log(`nc1 owner reset: ${readmitted.length} monitored device(s) re-admitted automatically`)
    } catch (e) {
      console.log('nc1 owner reset: automatic device re-admission failed - the devices re-post and are approved by name instead', e)
    }
  }

  console.log(`nc1 identity RESET complete for ${email} (${scope}, v${identityVersion}, passkey=${minted.passkeyRegistered})`)
  return {
    recoveryCode: minted.recoveryCode,
    recoveryCodeGrouped: minted.recoveryCode ? recoveryCodeDisplay(minted.recoveryCode) : null,
    recoveryCodeMintedAt: minted.recoveryCodeMintedAt,
    passkeyRegistered: minted.passkeyRegistered,
    identityVersion,
    resetAt: Number.isSafeInteger(response.resetAt) ? response.resetAt : null,
    purge: response.purge || null,
    resumed: !!response.resumed,
    readmitted,
  }
}

/** A refusal the service meant (bad request, re-auth, locked, unknown, conflict) — never retried or re-probed. */
function isFinalRefusal(e) {
  const status = e && e.response && e.response.status
  return status === 400 || status === 401 || status === 403 || status === 404 || status === 409
}

/**
 * Add a webauthn wrap to the account's identity bundle, signed by the cached
 * identity — the shared core of the required-mode login catch-up AND the
 * post-link enrollment (a freshly linked browser registering its own
 * passkey). `requireAbsent: true` keeps catch-up semantics (skip when ANY
 * webauthn wrap exists); false appends alongside existing wraps. The PRF
 * salt is FIXED PER ACCOUNT (unlock evaluates the PRF before knowing which
 * credential answers), so an existing wrap's salt is reused. Throws on hard
 * failure; returns { status: 'exists' | 'deferred' | 'enrolled' }.
 */
export async function enrollPasskeyWrap({ email, cached, acquirePasskey, required, requireAbsent }) {
  const bundle = (await E2eeApi.getIdentityBundle(email)).data
  const existing = (bundle.wraps && bundle.wraps.webauthn) || []
  if (requireAbsent && existing.length) {
    return { status: 'exists' }
  }

  const prfSaltB64 = existing.length ? existing[0].prfSalt : generatePrfSaltB64()
  const material = await acquirePasskey({ email, required: !!required, prfSaltB64 })
  if (!material) {
    return { status: 'deferred' }
  }

  const sodium = await getSodium()
  const secretsBytes = new TextEncoder().encode(JSON.stringify(cached.secrets))
  // The ceremony ran once; its wrap is appended to whatever the bundle holds
  // at write time, conditional on the rev the bundle was read with and
  // re-applied on a conflict (Wraps.writeBundle, 2026-10-08). A salt that
  // changed underneath us means another session minted the account's first
  // passkey meanwhile; this wrap would not unlock under that salt, so stop.
  const wrap = wrapToWebauthn(sodium, material.prfOutput, secretsBytes, {
    credentialId: material.credentialIdB64,
    prfSalt: prfSaltB64,
  })
  sodium.memzero(material.prfOutput)
  await writeBundle(email, (current) => {
    const now = (current.wraps && current.wraps.webauthn) || []
    if (now.length && now[0].prfSalt !== prfSaltB64) {
      throw new Error('A passkey was added from another session at the same time — please try again.')
    }
    const others = now.filter((w) => w.credentialId !== wrap.credentialId)
    return { ...current, wraps: { ...current.wraps, webauthn: [...others, wrap] } }
  }, { cached })
  // Remember that THIS browser minted the credential (UI hint only).
  await Keystore.recordLocalPasskey(email, material.credentialIdB64)
  return { status: 'enrolled' }
}

/**
 * Required-mode login catch-up: once VUE_APP_E2EE_PASSKEY_MODE flips to
 * `required`, accounts that deferred enrollment (identity bundle has no
 * webauthn wrap) must register a passkey at login. Only possible where this
 * browser holds the cached identity (we can't wrap secrets we don't have —
 * keyless browsers pick this up after unlock). Publishes the added wrap
 * through the signature-checked identityBundle write.
 * Returns { status: 'skipped' | 'exists' | 'unsupported' | 'enrolled' |
 * 'deferred' | 'failed' }. Never throws, never blocks login.
 */
export async function ensurePasskeyEnrollment({ email, acquirePasskey }) {
  try {
    if (passkeyMode() !== 'required' || !acquirePasskey || !E2eeApi.isConfigured()) {
      return { status: 'skipped' }
    }
    const cached = await Keystore.loadIdentity(email)
    if (!cached || !cached.secrets) {
      return { status: 'skipped' }
    }
    if (!(await prfProbablySupported())) {
      return { status: 'unsupported' }
    }

    const result = await enrollPasskeyWrap({
      email, cached, acquirePasskey, required: true, requireAbsent: true,
    })
    if (result.status === 'enrolled') {
      console.log(`nc1 passkey catch-up complete for ${email}`)
    }
    return result
  } catch (e) {
    console.log('nc1 passkey catch-up failed (will retry at next login)')
    console.log(e)
    return { status: 'failed' }
  }
}
